import USER_ORG_ROLES from '../../../utils/constants/userOrgRoles'
import USER_PERMISSIONS from '../../../utils/constants/userPermissions'
import INVITATION_TYPES from '../../../utils/constants/invitationTypes'
import { dutiesOfGrants, holdsDuty, managerCompetitionsOfGrants } from '../../../utils/constants/permissionSets'

const { OWNER, ADMIN, MEMBER } = USER_ORG_ROLES
// One collator for the sort: localeCompare with a locale builds a new one per comparison, which
// for ~1,500 names is most of the page's work.
const byName = new Intl.Collator('el', { sensitivity: 'base' }).compare

const initialsOf = (name) => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}

// Who the caller may act on, as the backend decides it: an Owner anyone but the Owner, an Admin
// only Members. Only an Owner changes someone's access; duties are only a Member's.
const rules = (level, callerRole, can, updatePermission, removePermission) => {
  const manageable = level !== OWNER && (callerRole === OWNER || (callerRole === ADMIN && level === MEMBER))
  return {
    manageable,
    canEditAccess: manageable && callerRole === OWNER && can(updatePermission),
    canEditDuties: manageable && level === MEMBER && can(updatePermission),
    canRemove: manageable && can(removePermission),
  }
}

/**
 * The users page's rows: members sorted Owner first, then everyone else by name; pending invitations
 * after them by email. `can(permission)` is the caller's server permission in this org.
 */
export const buildRows = ({ users, invitations, orgId, callerRole, can }) => {
  const members = (users || []).map((user) => {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
    const level = user.roles?.[orgId] || MEMBER
    return {
      key: `user-${user.id}`,
      kind: 'user',
      id: user.id,
      name,
      email: user.email,
      logoUrl: user.logoUrl,
      initials: initialsOf(name),
      level,
      duties: dutiesOfGrants(user.grants),
      managerCompetitions: managerCompetitionsOfGrants(user.grants),
      pending: false,
      canResend: false,
      ...rules(level, callerRole, can, USER_PERMISSIONS.UPDATE_USER, USER_PERMISSIONS.REMOVE_USER),
    }
  })

  const pending = (invitations || []).map((invitation) => {
    const { receiver = {} } = invitation
    const name = [receiver.firstName, receiver.lastName].filter(Boolean).join(' ')
    // Ownership and kick-off invitations are handled in org settings, not here.
    const membership = invitation.type === INVITATION_TYPES.MEMBERSHIP
    const level = invitation.role
    const allowed = membership
      ? rules(level, callerRole, can, USER_PERMISSIONS.UPDATE_INVITATION, USER_PERMISSIONS.DELETE_INVITATION)
      : { manageable: false, canEditAccess: false, canEditDuties: false, canRemove: false }
    return {
      key: `invitation-${invitation.id}`,
      kind: 'invitation',
      id: invitation.id,
      name: name || 'Invitation pending',
      email: receiver.email,
      logoUrl: receiver.logoUrl,
      initials: name ? initialsOf(name) : '',
      level,
      duties: dutiesOfGrants(invitation.grants),
      managerCompetitions: managerCompetitionsOfGrants(invitation.grants),
      pending: true,
      canResend: allowed.manageable && can(USER_PERMISSIONS.UPDATE_INVITATION),
      ...allowed,
    }
  })

  members.sort((a, b) => (b.level === OWNER) - (a.level === OWNER) || byName(a.name, b.name))
  pending.sort((a, b) => (a.email || '').localeCompare(b.email || ''))

  return { members, pending }
}

// Search by name or email; `admins` keeps only the Admins (and Admin invitations), and a duty
// filter keeps only the Members (and invitations) holding it, a Manager holding every duty.
export const filterRows = (rows, { query, duty, admins }) => {
  const q = (query || '').trim().toLowerCase()
  return rows.filter((row) => {
    if (admins && row.level !== ADMIN) return false
    if (duty && (row.level !== MEMBER || !holdsDuty(row.duties, duty))) return false
    return !q || row.name.toLowerCase().includes(q) || (row.email || '').toLowerCase().includes(q)
  })
}

// Big orgs (a federation's ~1,500 statisticians) are listed a page at a time: rendering every row
// at once makes the page and its search unusably slow.
export const PAGE_SIZE = 50

/** The rows on `page` (from 0), and the page actually shown once fewer rows are left. */
export const pageOf = (rows, page, size = PAGE_SIZE) => {
  const lastPage = Math.max(0, Math.ceil(rows.length / size) - 1)
  const current = Math.min(Math.max(page, 0), lastPage)
  return { rows: rows.slice(current * size, (current + 1) * size), page: current, lastPage }
}
